import { Link } from '@tanstack/react-router'
import { ArchiveIcon, ArrowLeftIcon, PlusIcon, Trash2Icon } from 'lucide-react'
import { useEffect, useMemo, useRef } from 'react'

import { Button, DataTable } from '@/components/ui'
import type { Row, Table } from '@/components/ui/table/types'
import { useAccount, useCheckout, useIsTrialAccount } from '@/modules/account'
import { isSingleProjectPlan } from '@/modules/account/utils'
import {
  CreateFolderDialog,
  createFolderColumns,
  DeleteFolderDialog,
  RenameFolderDialog,
  useFolders,
  useLoadingFolders,
} from '@/modules/folders'
import {
  type Project,
  useFetchProjects,
  useLoadingProjects,
  useProjects,
} from '@/modules/projects'
import {
  getProjectsColumns,
  ProjectProvider,
} from '@/modules/projects/components'
import { useDialogActions, useSubmittedDialog } from '@/stores/dialog'
import { cn } from '@/utils'
import { isProjectDeletable } from '../expiration'
import { isProjectBusy } from '../status'
import {
  CreateProjectDialog,
  DeleteProjectsDialog,
  EditProjectDialog,
} from './dialogs'
import { archiveProjects } from './tables/projects/archiveProjects'

interface ProjectsListProps {
  folderSlug?: string
}

export const ProjectsList = ({ folderSlug }: ProjectsListProps) => {
  const table = useRef<Table<Project> | null>(null)
  const folders = useFolders()
  const projects = useProjects()
  const isLoadingProjects = useLoadingProjects()
  const isLoadingFolders = useLoadingFolders()
  const { openDialog } = useDialogActions()
  const submittedDialog = useSubmittedDialog()
  const account = useAccount()
  const isTrial = useIsTrialAccount()
  const { buyProject } = useCheckout()
  useFetchProjects(folderSlug)

  useEffect(() => {
    if (submittedDialog && table.current) {
      table.current.resetRowSelection()
    }
  }, [submittedDialog, table])

  // An archived row leaves the list, so its selection must not linger.
  useEffect(() => {
    table.current?.resetRowSelection()
  }, [projects])

  const columns = useMemo(
    () => getProjectsColumns(account?.planKind),
    [account?.planKind],
  )

  const isProjectSelectable = (row: Row<Project>) =>
    !isProjectBusy(row.original) &&
    isProjectDeletable(row.original, account?.planKind)

  const canCreateProject = !isSingleProjectPlan(account?.planKind)

  const addProjectButton = canCreateProject
    ? {
        label: 'Create new project',
        onClick: () => openDialog(CreateProjectDialog.id),
      }
    : { label: 'Buy project', onClick: buyProject }

  return (
    <>
      <CreateProjectDialog />
      <CreateFolderDialog />
      <DeleteProjectsDialog />
      <DeleteFolderDialog />
      <RenameFolderDialog />
      <ProjectProvider>
        <EditProjectDialog />
      </ProjectProvider>

      <div className="rounded-lg border overflow-hidden">
        <div>
          {!folderSlug && !isTrial && (
            <>
              <div className="bg-neutral-50 border-b px-4 py-2 flex items-center justify-between">
                <h2 className="text-sm font-semibold text-blue-7 uppercase tracking-wide">
                  Folders
                </h2>
                <Button
                  size="sm"
                  className="gap-1.5 h-7 text-xs"
                  onClick={() =>
                    openDialog(CreateFolderDialog.id, { kind: 'project' })
                  }
                >
                  <PlusIcon className="h-3.5 w-3.5" />
                  Create new folder
                </Button>
              </div>

              <DataTable
                isLoading={isLoadingFolders}
                columns={createFolderColumns('project')}
                data={folders}
                height="h-auto"
                emptyMessage="You don't have any folders yet"
              />
            </>
          )}

          {(folderSlug || !isTrial) && (
            <div
              className={cn(
                'bg-neutral-50 border-b px-4 py-2 flex items-center justify-between',
                !folderSlug && 'border-t justify-end',
              )}
            >
              {folderSlug && (
                <div className="flex items-center gap-2">
                  <Link
                    to="/projects"
                    aria-label="Back to all folders and projects"
                    className="text-muted-foreground hover:text-foreground hover:bg-neutral-200/60 rounded p-1 cursor-pointer transition-colors"
                  >
                    <ArrowLeftIcon className="size-4" />
                  </Link>
                  <h2 className="text-sm font-semibold text-blue-7 uppercase tracking-wide">
                    Projects
                  </h2>
                </div>
              )}
              {!isTrial && (
                <Button
                  size="sm"
                  className="gap-1.5 h-7 text-xs"
                  onClick={addProjectButton.onClick}
                >
                  <PlusIcon className="size-3.5" />
                  {addProjectButton.label}
                </Button>
              )}
            </div>
          )}

          <DataTable
            columns={columns}
            data={projects}
            enableRowSelection={isProjectSelectable}
            isLoading={isLoadingProjects}
            emptyMessage="You don't have any projects yet"
            height="h-auto"
            onTableReady={(value) => {
              table.current = value
            }}
            bulkActions={(selection) => (
              <>
                {!isTrial && (
                  <Button
                    theme="outline"
                    size="sm"
                    onClick={() =>
                      archiveProjects(
                        projects.filter((project) => selection[project.id]),
                      )
                    }
                    className="h-8 gap-1.5 text-xs font-medium"
                  >
                    <ArchiveIcon className="size-3.5" />
                    Archive
                  </Button>
                )}
                <Button
                  theme="outline"
                  size="sm"
                  onClick={() =>
                    openDialog(
                      DeleteProjectsDialog.id,
                      projects.filter((project) => selection[project.id]),
                    )
                  }
                  className="h-8 gap-1.5 text-xs font-medium text-red-600 hover:text-red-700 hover:bg-red-50"
                >
                  <Trash2Icon className="size-3.5" />
                  Delete
                </Button>
              </>
            )}
          />
        </div>
      </div>
    </>
  )
}
